import { groupBy } from 'lodash'

import { ShiftActivity } from '@bemlo/enums'
import { OnCallTime, getShiftHours } from '@bemlo/schedule'
import { Falsy } from '@bemlo/utils'

import { byCellFilter } from '../by-filter'
import { Cell } from '../components/Cell'
import { CellShiftsColumn } from '../components/CellShiftsColumn'
import { DraftShifts } from '../components/DraftShifts'
import { NewShift } from '../components/NewShift'
import { Row } from '../components/Row'
import { CellStatus } from '../components/useCellTheme'
import {
  ShiftPresetPersist,
  VacanciesTitle,
} from '../components/VacanciesTitle'
import { useEditShifts } from '../useEditShifts'
import { useSchedule } from '../useSchedule'
import { getShiftsInWeek } from '../utils/get-shifts-in-week'

// DRAFT is handled separately
// TODO: add BOOKED if compact mode is enabled
const CELL_STATUSES: CellStatus[] = [
  'ARCHIVED',
  'VACANT',
  'APPLIED',
  'SUGGESTED',
  'PRESENTED',
  'ACCEPTED',
  'BOOKED',
]

const toId = <T extends { id: string }>(value: T) => value.id

type VacanciesProps = {
  title?: string
  persisted?: ShiftPresetPersist | Falsy
  activity: ShiftActivity
  startTime: string
  endTime: string
  breakTime: number
  comment: string
  rowKey?: number | undefined
  onCallTime: OnCallTime | null | undefined
  tags: { id: string }[]
  scheduleResourceId: string
  includeBooked?: boolean
  showBreakBadge: boolean
}

export const Vacancies = (props: VacanciesProps) => {
  const {
    startDate,
    days,
    currentWeek,
    shifts: allShifts,
    shiftPopover,
    isScrolled,
  } = useSchedule()

  const { enableEditing, onCreateDraftShift, onDeleteDraftShift } =
    useEditShifts(props)

  const shifts = allShifts.filter(byCellFilter(props))
  const shiftsToDisplay = props.includeBooked
    ? shifts
    : shifts.filter(({ bookedCandidateId }) => !bookedCandidateId)
  const shiftsInWeek = currentWeek
    ? getShiftsInWeek(currentWeek, shiftsToDisplay)
    : shiftsToDisplay

  if (!shifts.length && !enableEditing && !props.persisted) return null

  return (
    <Row>
      <Cell isTitle rowSpan={1} rightShadow={isScrolled}>
        <VacanciesTitle
          {...props}
          shiftHours={shiftsInWeek[0] ? getShiftHours(shiftsInWeek[0]) : null}
          startDate={startDate}
          persisted={props.persisted}
          shiftIds={shiftsInWeek.map(toId)}
        />
      </Cell>
      {days.map((day) => {
        const shiftsOnDay = groupBy(
          shiftsToDisplay.filter((shift) => shift.date === day.dateISO),
          'status',
        )
        const draftShifts = shiftsOnDay['DRAFT'] ?? []

        return (
          <Cell
            key={day.dateISO}
            disabled={!day.withinRange}
            weekend={day.isWeekend}
          >
            <CellShiftsColumn>
              {CELL_STATUSES.map((status) => {
                const shifts = shiftsOnDay[status] ?? []
                const isArchived = status === 'ARCHIVED'
                const isUrgent =
                  shifts.every((shift) => shift.isUrgent) && !isArchived
                if (!shifts.length) return null
                return (
                  <NewShift
                    key={status}
                    shift={shifts[0]}
                    interestedCandidates={shifts[0].interestedCandidates}
                    popoverContent={shiftPopover}
                    shiftIds={shifts.map(toId)}
                    status={status}
                    isUrgent={isUrgent}
                    isPublished={shifts.some((s) => s.isPublished)}
                    isTendered={shifts.some((s) => s.isTendered)}
                    unselectable={isArchived}
                  />
                )
              })}
              {enableEditing && day.withinRange && (
                <DraftShifts
                  showBadges
                  shifts={draftShifts}
                  onClickNewShift={() =>
                    onCreateDraftShift(day.dateISO, shifts[0]?.scheduleSlotId)
                  }
                  onDeleteDraftShift={onDeleteDraftShift}
                />
              )}
            </CellShiftsColumn>
          </Cell>
        )
      })}
    </Row>
  )
}
